import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useArticles } from "@/hooks/useArticles";
import SectionHeader from "@/components/base/SectionHeader";

export default function InnovationsSection() {
  const { t } = useTranslation();
  const { articles: innovations, loading } = useArticles({ category: "Innovation", limit: 4 });

  if (loading) {
    return (
      <section className="py-16 bg-[#fdf8f3]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-56 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="md:col-span-2 bg-gray-200 rounded-2xl h-80"></div>
              {[1, 2, 3].map((i) => <div key={i} className="bg-gray-200 rounded-2xl h-56"></div>)}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-[#fdf8f3]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionHeader
          title={t("section.innovations.title")}
          subtitle={t("section.innovations.subtitle")}
          viewAllLink="/innovations"
          accent="yellow"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {innovations.map((item, index) => (
            <Link
              key={item.id}
              to={`/article/${item.id}`}
              className={`group cursor-pointer block ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
            >
              {index === 0 ? (
                <div className="relative rounded-2xl overflow-hidden h-full" style={{ minHeight: "280px" }}>
                  <img src={item.image} alt={item.title} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full">Innovation</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex items-center gap-2 text-yellow-400 text-sm mb-2">
                      <i className="ri-map-pin-line"></i>
                      <span>{item.countryFlag} {item.country} · {item.sector}</span>
                    </div>
                    <h3 className="text-white font-bold text-xl leading-tight mb-2 line-clamp-2">{item.title}</h3>
                    <p className="text-white/70 text-sm line-clamp-2">{item.summary}</p>
                    <div className="mt-4 flex items-center gap-2 text-white/60 text-xs">
                      <i className="ri-user-line"></i>
                      <span>{item.entrepreneur}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-yellow-200 hover:-translate-y-1 transition-all duration-300 h-full">
                  <div className="relative h-40 overflow-hidden">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 left-3">
                      <span className="bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full">Innovation</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                      <span>{item.countryFlag} {item.country}</span>
                      <span>·</span>
                      <span>{item.sector}</span>
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 group-hover:text-yellow-600 transition-colors mb-2">{item.title}</h3>
                    <p className="text-xs text-gray-500 line-clamp-2">{item.summary}</p>
                  </div>
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
